<template>
  <div v-if="visible" class="manga-edit-overlay">
    <div class="manga-edit-dialog">
      <header>
        <div class="title">漫画翻译编辑器<small id="docName"></small></div>
        <div class="group">
          <label>底图</label>
          <select id="baseSel">
            <option value="inpaint">修复图（编辑器显示基准）</option>
            <option value="orig">翻译结果图</option>
          </select>
        </div>
        <div class="group">
          <label><input type="checkbox" id="cbText" checked>译文</label>
        </div>
        <div class="group">
          <button id="zoomOut" title="缩小">−</button>
          <span id="zoomLabel">—</span>
          <button id="zoomIn" title="放大">＋</button>
          <button id="zoomFit">适应窗口</button>
          <button id="zoom100">100%</button>
        </div>
        <div class="group">
          <button id="fontBtn" title="加载本地字体文件(.ttf/.otf)以贴近原版渲染">加载字体…</button>
          <input type="file" id="fontFile" accept=".ttf,.otf,.woff,.woff2">
          <button id="exportBtn" class="primary">导出</button>
          <button class="close-btn" @click="close">关闭</button>
        </div>
      </header>
      <main>
        <aside id="sidebar">
          <div class="head" id="sideHead">区域列表</div>
          <div id="editPanel">
            <div class="ep-title" id="epTitle">编辑区域</div>
            <textarea id="epText" spellcheck="false" placeholder="输入译文，[BR] 表示换行/分列"></textarea>
            <div class="ep-row">
              <label>字号</label>
              <input type="range" id="epSize" min="8" max="160" step="1">
              <span id="epSizeVal">—</span>
              <input type="number" id="epSizeNum" min="8" max="160" step="1">
            </div>
            <div class="ep-row">
              <label>方向</label>
              <div class="seg" id="epDir">
                <button data-d="h">横排</button>
                <button data-d="v">竖排</button>
              </div>
            </div>
            <div class="ep-row">
              <label>对齐</label>
              <div class="seg" id="epAlign">
                <button data-a="left" id="epAlignL" title="横排靠左 / 竖排靠上">左</button>
                <button data-a="center" id="epAlignC" title="居中">中</button>
                <button data-a="right" id="epAlignR" title="横排靠右 / 竖排靠下">右</button>
              </div>
            </div>
            <div class="ep-hint">拖动蓝框内移动位置，拖四角/四边缩放字号</div>
            <button id="epReset">恢复该区域原始值</button>
          </div>
          <div id="regionList"></div>
        </aside>
        <div id="viewport">
          <div id="canvasWrap">
            <canvas class="base" id="baseCanvas"></canvas>
            <canvas class="over" id="overCanvas"></canvas>
          </div>
          <div id="empty">
            <span class="empty-spinner"></span>
            <span class="empty-text">正在加载图片…</span>
          </div>
        </div>
      </main>
      <footer>
        <span id="stState">加载中…</span>
        <span id="stRegions"></span>
        <span id="stFont"></span>
        <span id="fontInfo"></span>
      </footer>
    </div>
  </div>
</template>

<script setup>
import { watch, nextTick } from 'vue'
import { initMangaEdit } from './mangaEditEngine.js'

const props = defineProps({
  visible: { type: Boolean, default: false },
  // 历史记录行：{ id, fileName, translationJson }
  row: { type: Object, default: null },
  // nginx 静态根，window.global_config.previewId
  previewId: { type: String, default: '' }
})

const emit = defineEmits(['close'])

const close = () => emit('close')

watch(() => props.visible, async (val) => {
  if (!val) return
  await nextTick()
  const row = props.row
  if (!row || !row.translationJson) return

  let pageData
  try {
    pageData = JSON.parse(row.translationJson)
  } catch (e) {
    console.error('translationJson 解析失败', e)
    return
  }

  const ext = (row.fileName || '').substring((row.fileName || '').lastIndexOf('.')) || '.png'
  const origSrc = `${props.previewId}/${row.id}${ext}`
  const inpaintSrc = `${props.previewId}/edit/${row.id}_inpainted${ext}`

  initMangaEdit({
    pageData,
    origSrc,
    inpaintSrc,
    exportExt: ext,
    fileBaseName: row.fileName || String(row.id)
  })
})
</script>

<style scoped>
.manga-edit-overlay {
  position: fixed;
  inset: 0;
  z-index: 10000;
  background: rgba(15, 23, 42, 0.85);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.manga-edit-dialog {
  width: 100%;
  height: 100%;
  max-width: 1600px;
  display: flex;
  flex-direction: column;
  background: #1b1d21;
  color: #e6e9ee;
  border: 1px solid #3a3f47;
  border-radius: 12px;
  overflow: hidden;
  font: 13px/1.5 'Segoe UI', 'Microsoft YaHei', system-ui, sans-serif;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
}

.manga-edit-dialog :deep(header) {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 8px 14px;
  background: #24272c;
  border-bottom: 1px solid #3a3f47;
  flex-wrap: wrap;
}

.manga-edit-dialog :deep(header .title) {
  font-weight: 600;
  white-space: nowrap;
}

.manga-edit-dialog :deep(header .title small) {
  color: #9aa3af;
  font-weight: 400;
  margin-left: 8px;
}

.manga-edit-dialog :deep(.group) {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 10px;
  border-left: 1px solid #3a3f47;
}

.manga-edit-dialog :deep(.group label) {
  display: flex;
  align-items: center;
  gap: 4px;
  color: #9aa3af;
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}

.manga-edit-dialog :deep(.group label:hover) {
  color: #e6e9ee;
}

.manga-edit-dialog :deep(button),
.manga-edit-dialog :deep(select) {
  background: #2b2f36;
  color: #e6e9ee;
  border: 1px solid #3a3f47;
  border-radius: 6px;
  padding: 4px 10px;
  font: inherit;
  cursor: pointer;
}

.manga-edit-dialog :deep(button:hover),
.manga-edit-dialog :deep(select:hover) {
  border-color: #4f8cff;
}

.manga-edit-dialog :deep(button.primary) {
  background: #4f8cff;
  border-color: #4f8cff;
  color: #fff;
}

.manga-edit-dialog :deep(.close-btn) {
  background: #3a3f47;
}

.manga-edit-dialog :deep(#zoomLabel) {
  min-width: 46px;
  text-align: center;
  color: #9aa3af;
}

.manga-edit-dialog :deep(main) {
  flex: 1;
  display: flex;
  min-height: 0;
}

.manga-edit-dialog :deep(#sidebar) {
  width: 270px;
  min-width: 270px;
  background: #24272c;
  border-right: 1px solid #3a3f47;
  display: flex;
  flex-direction: column;
}

.manga-edit-dialog :deep(#sidebar .head) {
  padding: 8px 12px;
  border-bottom: 1px solid #3a3f47;
  color: #9aa3af;
}

.manga-edit-dialog :deep(#regionList) {
  flex: 1;
  overflow: auto;
  padding: 6px;
}

.manga-edit-dialog :deep(.ritem) {
  padding: 7px 9px;
  margin-bottom: 5px;
  border: 1px solid #3a3f47;
  border-radius: 7px;
  cursor: pointer;
  background: #2b2f36;
  transition: border-color 0.1s;
}

.manga-edit-dialog :deep(.ritem:hover) {
  border-color: #555c66;
}

.manga-edit-dialog :deep(.ritem.sel) {
  border-color: #4f8cff;
  box-shadow: 0 0 0 1px #4f8cff inset;
}

.manga-edit-dialog :deep(.ritem .row1) {
  display: flex;
  justify-content: space-between;
  gap: 6px;
  color: #9aa3af;
  font-size: 11px;
}

.manga-edit-dialog :deep(.ritem .txt) {
  margin-top: 3px;
  color: #e6e9ee;
  word-break: break-all;
}

.manga-edit-dialog :deep(.ritem .txt.src) {
  color: #9aa3af;
  font-size: 12px;
}

.manga-edit-dialog :deep(#viewport) {
  flex: 1;
  overflow: auto;
  position: relative;
  background: #141518;
  display: flex;
  padding: 24px;
}

.manga-edit-dialog :deep(#canvasWrap) {
  position: relative;
  transform-origin: 0 0;
  display: block;
  margin: auto;
}

.manga-edit-dialog :deep(#canvasWrap canvas) {
  display: block;
  position: absolute;
  left: 0;
  top: 0;
}

.manga-edit-dialog :deep(#canvasWrap canvas.base) {
  position: relative;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.5);
}

.manga-edit-dialog :deep(#empty) {
  position: absolute;
  inset: 0;
  display: none;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  color: #9aa3af;
  background: rgba(20, 21, 24, 0.75);
}

.manga-edit-dialog :deep(#empty .empty-spinner) {
  width: 36px;
  height: 36px;
  border: 3px solid #3a3f47;
  border-top-color: #4f8cff;
  border-radius: 50%;
  animation: edit-loading-spin 0.8s linear infinite;
}

.manga-edit-dialog :deep(#empty .empty-text) {
  font-size: 13px;
  letter-spacing: 0.5px;
}

.manga-edit-dialog :deep(#empty.error .empty-spinner) {
  display: none;
}

@keyframes edit-loading-spin {
  to {
    transform: rotate(360deg);
  }
}

.manga-edit-dialog :deep(footer) {
  display: flex;
  gap: 18px;
  padding: 5px 14px;
  background: #24272c;
  border-top: 1px solid #3a3f47;
  color: #9aa3af;
  font-size: 12px;
}

.manga-edit-dialog :deep(footer .ok) {
  color: #7ec699;
}

.manga-edit-dialog :deep(footer .warn) {
  color: #e6b450;
}

.manga-edit-dialog :deep(#fontInfo) {
  margin-left: auto;
}

.manga-edit-dialog :deep(input[type='file']) {
  display: none;
}

.manga-edit-dialog :deep(#editPanel) {
  display: none;
  padding: 10px 12px;
  border-bottom: 1px solid #3a3f47;
  background: #2b2f36;
}

.manga-edit-dialog :deep(#editPanel.show) {
  display: block;
}

.manga-edit-dialog :deep(#editPanel .ep-title) {
  font-weight: 600;
  margin-bottom: 6px;
  color: #4f8cff;
}

.manga-edit-dialog :deep(#epText) {
  width: 100%;
  box-sizing: border-box;
  min-height: 64px;
  resize: vertical;
  background: #1b1d21;
  color: #e6e9ee;
  border: 1px solid #3a3f47;
  border-radius: 6px;
  padding: 6px 8px;
  font: inherit;
}

.manga-edit-dialog :deep(#epText:focus) {
  outline: none;
  border-color: #4f8cff;
}

.manga-edit-dialog :deep(.ep-row) {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  color: #9aa3af;
}

.manga-edit-dialog :deep(.ep-row label) {
  flex-shrink: 0;
  white-space: nowrap;
}

.manga-edit-dialog :deep(.ep-row input[type='range']) {
  flex: 1;
  min-width: 60px;
  accent-color: #4f8cff;
}

.manga-edit-dialog :deep(.ep-row input[type='number']) {
  width: 58px;
  background: #1b1d21;
  color: #e6e9ee;
  border: 1px solid #3a3f47;
  border-radius: 6px;
  padding: 3px 5px;
  font: inherit;
}

.manga-edit-dialog :deep(#epSizeVal) {
  min-width: 28px;
  text-align: right;
  color: #e6e9ee;
}

.manga-edit-dialog :deep(.seg) {
  display: flex;
  gap: 0;
  border: 1px solid #3a3f47;
  border-radius: 6px;
  overflow: hidden;
}

.manga-edit-dialog :deep(.seg button) {
  border: none;
  border-radius: 0;
  background: #1b1d21;
  padding: 3px 14px;
}

.manga-edit-dialog :deep(.seg button + button) {
  border-left: 1px solid #3a3f47;
}

.manga-edit-dialog :deep(.seg button.on) {
  background: #4f8cff;
  color: #fff;
}

.manga-edit-dialog :deep(.ep-hint) {
  margin-top: 8px;
  color: #9aa3af;
  font-size: 11px;
  line-height: 1.4;
}

.manga-edit-dialog :deep(#epReset) {
  margin-top: 8px;
  width: 100%;
}
</style>
